import { useRef, useState } from 'react'
import {
  GLOSSARY_UPLOAD_MAX_BYTES,
  type GlossaryDictionaryUploadResult,
  type GlossaryDuplicateTermsDetails,
  type GlossaryErrorCode,
  type GlossarySkippedRow,
} from '@ai-meeting/shared'
import {
  DocumentIcon,
  ExclamationTriangleIcon,
  UploadIcon,
  XMarkIcon,
} from '@/components/Icons'
import { fmt } from '@/constants/ui-locale'
import { useUiStrings } from '@/hooks/useUiStrings'
import { ApiError } from '@/services/api'
import { logActivity } from '@/services/activity'

type UploadStatus = 'pending' | 'local-invalid' | 'uploading' | 'success' | 'failed'
type LocalError = 'invalid_type' | 'file_too_large'

type QueuedFile = {
  id: string
  file: File
  status: UploadStatus
  localError?: LocalError
  result?: GlossaryDictionaryUploadResult
  error?: {
    code?: GlossaryErrorCode
    message: string
    details?: unknown
  }
  detailsOpen: boolean
}

interface UploadDictionaryModalProps {
  uploading: boolean
  onClose: () => void
  onUpload: (file: File) => Promise<GlossaryDictionaryUploadResult>
}

function fileKey(file: File): string {
  return `${file.name}-${file.size}-${file.lastModified}`
}

function validateFile(file: File): LocalError | undefined {
  if (!file.name.toLowerCase().endsWith('.csv')) return 'invalid_type'
  if (file.size > GLOSSARY_UPLOAD_MAX_BYTES) return 'file_too_large'
  return undefined
}

function createQueuedFile(file: File): QueuedFile {
  const localError = validateFile(file)
  return {
    id: `${fileKey(file)}-${crypto.randomUUID()}`,
    file,
    status: localError ? 'local-invalid' : 'pending',
    localError,
    detailsOpen: false,
  }
}

function SuccessIcon() {
  return (
    <svg
      viewBox="0 0 20 20"
      fill="none"
      aria-hidden
      className="h-4 w-4 shrink-0 text-emerald-600 dark:text-emerald-400"
    >
      <circle cx="10" cy="10" r="7" stroke="currentColor" strokeWidth="1.8" />
      <path
        d="m6.7 10.1 2.1 2.1 4.5-4.6"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

export default function UploadDictionaryModal({
  uploading,
  onClose,
  onUpload,
}: UploadDictionaryModalProps) {
  const { t } = useUiStrings()
  const inputRef = useRef<HTMLInputElement>(null)
  const replacementInputRef = useRef<HTMLInputElement>(null)
  const [files, setFiles] = useState<QueuedFile[]>([])
  const [dragging, setDragging] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const [phase, setPhase] = useState<'selection' | 'results'>('selection')
  const [replacementId, setReplacementId] = useState<string | null>(null)

  function addFiles(incoming: FileList | File[]) {
    const nextFiles = Array.from(incoming)
    if (nextFiles.length === 0) return

    setFiles((current) => {
      const existingKeys = new Set(current.map((item) => fileKey(item.file)))
      const nextItems = nextFiles
        .filter((file) => !existingKeys.has(fileKey(file)))
        .map(createQueuedFile)
      return [...current, ...nextItems]
    })
  }

  function removeFile(id: string) {
    setFiles((current) => current.filter((item) => item.id !== id))
  }

  async function uploadFile(item: QueuedFile) {
    setFiles((current) =>
      current.map((entry) =>
        entry.id === item.id
          ? { ...entry, status: 'uploading', error: undefined, detailsOpen: false }
          : entry,
      ),
    )
    try {
      const result = await onUpload(item.file)
      // One row per file rather than per press of Upload: a queue of five is
      // five uses of the feature, and a report that counted the press would
      // say one (BRAI-677).
      logActivity('glossary.uploaded')
      setFiles((current) =>
        current.map((entry) =>
          entry.id === item.id
            ? { ...entry, status: 'success', result, error: undefined }
            : entry,
        ),
      )
    } catch (err) {
      logActivity('glossary.uploaded', { status: 'failure' })
      const apiError = err instanceof ApiError ? err : null
      setFiles((current) =>
        current.map((entry) =>
          entry.id === item.id
            ? {
                ...entry,
                status: 'failed',
                result: undefined,
                error: {
                  code: apiError?.code as GlossaryErrorCode | undefined,
                  message: apiError?.message ?? t.glossaryUploadFailed,
                  details: apiError?.details,
                },
              }
            : entry,
        ),
      )
    }
  }

  async function handleUpload() {
    if (uploading || isUploading) return
    const queue = files.filter((item) => item.status === 'pending')
    if (queue.length === 0) return

    setPhase('results')
    setIsUploading(true)
    for (const item of queue) {
      await uploadFile(item)
    }
    setIsUploading(false)
  }

  async function replaceAndRetry(id: string, file: File) {
    const replacement = { ...createQueuedFile(file), id }
    setFiles((current) => current.map((item) => (item.id === id ? replacement : item)))
    setReplacementId(null)
    if (replacement.status !== 'pending') return

    setIsUploading(true)
    await uploadFile(replacement)
    setIsUploading(false)
  }

  function toggleDetails(id: string) {
    setFiles((current) =>
      current.map((item) =>
        item.id === id ? { ...item, detailsOpen: !item.detailsOpen } : item,
      ),
    )
  }

  function localErrorLabel(error?: LocalError): string {
    return error === 'invalid_type' ? t.glossaryUploadNotCsv : t.glossaryUploadFileTooLarge
  }

  function failureLabel(item: QueuedFile): string {
    if (item.status === 'local-invalid') return localErrorLabel(item.localError)
    const code = item.error?.code
    const details = item.error?.details as { conflictCount?: number } | undefined
    if (code === 'GLOSSARY_DUPLICATE_TERMS') {
      const count = details?.conflictCount ?? 0
      return count === 1
        ? t.glossaryUploadTermConflict
        : fmt(t.glossaryUploadTermsConflict, { count })
    }
    if (code === 'GLOSSARY_TOO_MANY_ROWS') return t.glossaryUploadTooManyRows
    if (code === 'GLOSSARY_COLUMN_MISMATCH') return t.glossaryUploadWrongFormat
    if (code === 'GLOSSARY_EMPTY_CELL') return t.glossaryUploadEmptyCells
    if (code === 'GLOSSARY_EMPTY_FILE') return t.glossaryUploadEmptyFile
    if (code === 'GLOSSARY_INVALID_FILE_TYPE') return t.glossaryUploadNotCsv
    if (code === 'GLOSSARY_FILE_TOO_LARGE') return t.glossaryUploadFileTooLarge
    return item.error?.message ?? t.glossaryUploadFailed
  }

  function hasDetails(item: QueuedFile): boolean {
    if (item.status === 'success') return Boolean(item.result?.skippedRows.length)
    return (
      item.error?.code === 'GLOSSARY_DUPLICATE_TERMS' ||
      item.error?.code === 'GLOSSARY_COLUMN_MISMATCH' ||
      item.error?.code === 'GLOSSARY_EMPTY_CELL'
    )
  }

  function renderSkippedRows(rows: GlossarySkippedRow[]) {
    return rows.map((row) => (
      <li key={`${row.row}-${row.field}`}>
        {fmt(t.glossaryUploadRowDetail, {
          row: row.row,
          field:
            row.field === 'term' ? t.glossaryUploadFieldTerm : t.glossaryUploadFieldMeaning,
          reason: t.glossaryUploadReasonTooLong,
        })}
      </li>
    ))
  }

  function renderErrorDetails(item: QueuedFile) {
    const details = item.error?.details
    if (!details || typeof details !== 'object') return null

    if (item.error?.code === 'GLOSSARY_DUPLICATE_TERMS') {
      const conflicts = (details as GlossaryDuplicateTermsDetails).conflicts ?? []
      return conflicts.map((conflict) => (
        <li key={conflict.term}>
          {fmt(t.glossaryUploadConflictDetail, {
            term: conflict.term,
            rows: conflict.rows.join(', '),
            meanings: conflict.meanings.join(' / '),
          })}
        </li>
      ))
    }

    const column = details as {
      row?: number
      columnIndex?: number
      expectedName?: string
      actualName?: string
      expectedColumnCount?: number
      actualColumnCount?: number
      field?: 'term' | 'meaning'
    }
    if (item.error?.code === 'GLOSSARY_COLUMN_MISMATCH') {
      if (column.expectedName != null) {
        return (
          <li>
            {fmt(t.glossaryUploadColumnExpected, {
              column: column.columnIndex ?? 1,
              actual: column.actualName ?? '',
              expected: column.expectedName,
            })}
          </li>
        )
      }
      return (
        <li>
          {fmt(t.glossaryUploadColumnCount, {
            expected: column.expectedColumnCount ?? 2,
            actual: column.actualColumnCount ?? 0,
          })}
        </li>
      )
    }

    if (item.error?.code === 'GLOSSARY_EMPTY_CELL' && column.row && column.field) {
      return (
        <li>
          {fmt(t.glossaryUploadRowDetail, {
            row: column.row,
            field:
              column.field === 'term'
                ? t.glossaryUploadFieldTerm
                : t.glossaryUploadFieldMeaning,
            reason: t.glossaryUploadEmptyCells,
          })}
        </li>
      )
    }
    return null
  }

  const busy = uploading || isUploading
  const pendingCount = files.filter((item) => item.status === 'pending').length
  const uploadedCount = files.filter((item) => item.status === 'success').length

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-slate-950/40 px-3 py-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="upload-dictionary-title"
    >
      <button
        type="button"
        aria-label={t.close}
        className="fixed inset-0 cursor-default"
        onClick={busy ? undefined : onClose}
      />

      <div className="animate-scale-in relative w-full max-w-md rounded-2xl border border-[var(--border-color)] bg-[var(--bg-secondary)] p-5 shadow-2xl sm:p-6">
        <h2
          id="upload-dictionary-title"
          className="text-lg font-bold text-[var(--text-primary)]"
        >
          {phase === 'selection'
            ? t.glossaryUploadDictionary
            : fmt(t.glossaryUploadSummary, {
                uploaded: uploadedCount,
                total: files.length,
              })}
        </h2>
        <p className="mt-1 text-xs text-[var(--text-tertiary)]">
          {phase === 'selection'
            ? t.glossaryUploadConstraints
            : t.glossaryUploadSummaryHint}
        </p>

        {phase === 'selection' && files.length === 0 && (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            onDragEnter={(event) => {
              event.preventDefault()
              setDragging(true)
            }}
            onDragOver={(event) => {
              event.preventDefault()
              setDragging(true)
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={(event) => {
              event.preventDefault()
              setDragging(false)
              if (event.dataTransfer.files.length > 0) addFiles(event.dataTransfer.files)
            }}
            className={[
              'mt-5 flex w-full flex-col items-center justify-center rounded-xl border border-dashed px-4 py-8 transition-colors',
              dragging
                ? 'border-[var(--accent)] bg-[var(--accent-light)]'
                : 'border-[var(--border-color)] bg-[var(--bg-primary)] hover:bg-[var(--bg-tertiary)]',
            ].join(' ')}
          >
            <UploadIcon className="h-6 w-6 text-[var(--accent)]" />
            <span className="mt-2 text-sm font-medium text-[var(--text-secondary)]">
              {t.glossaryUploadDragDrop}
            </span>
          </button>
        )}
        <input
          ref={inputRef}
          multiple
          type="file"
          accept=".csv,text/csv"
          className="hidden"
          onChange={(event) => {
            if (event.target.files) addFiles(event.target.files)
            event.target.value = ''
          }}
        />

        <input
          ref={replacementInputRef}
          type="file"
          className="hidden"
          onChange={(event) => {
            const replacement = event.target.files?.[0]
            if (replacementId && replacement) {
              void replaceAndRetry(replacementId, replacement)
            }
            event.target.value = ''
          }}
        />

        {files.length > 0 && (
          <div
            className="mt-4 max-h-[min(55vh,32rem)] space-y-2 overflow-y-auto pr-0.5"
            aria-live="polite"
          >
            {files.map((item) => (
              <div
                key={item.id}
                className={[
                  'rounded-lg border bg-[var(--bg-primary)] px-3 py-2.5',
                  item.status === 'local-invalid' || item.status === 'failed'
                    ? 'border-red-400 dark:border-red-700'
                    : 'border-[var(--border-color)]',
                ].join(' ')}
              >
                <div className="flex items-center gap-2">
                  {phase === 'selection' ? (
                    <DocumentIcon className="h-4 w-4 shrink-0 text-[var(--text-tertiary)]" />
                  ) : item.status === 'success' ? (
                    <SuccessIcon />
                  ) : item.status === 'uploading' ? (
                    <span className="h-4 w-4 shrink-0 animate-spin rounded-full border-2 border-[var(--accent)] border-r-transparent motion-reduce:animate-none" />
                  ) : (
                    <ExclamationTriangleIcon className="h-4 w-4 shrink-0 text-red-600 dark:text-red-400" />
                  )}
                  <span className="min-w-0 flex-1 truncate text-xs font-semibold text-[var(--text-primary)]">
                    {item.file.name}
                  </span>
                  {phase === 'selection' ? (
                    <>
                      {item.status === 'local-invalid' && (
                        <span className="shrink-0 rounded-full bg-red-100 px-2 py-0.5 text-[11px] font-medium text-red-700 dark:bg-red-950/60 dark:text-red-300">
                          {localErrorLabel(item.localError)}
                        </span>
                      )}
                      <button
                        type="button"
                        onClick={() => removeFile(item.id)}
                        disabled={busy}
                        className="rounded p-1 text-[var(--text-tertiary)] hover:bg-[var(--bg-tertiary)] disabled:opacity-50"
                        aria-label={`${t.clear} ${item.file.name}`}
                      >
                        <XMarkIcon className="h-3.5 w-3.5" />
                      </button>
                    </>
                  ) : (
                    <span
                      className={[
                        'shrink-0 text-[11px] font-medium',
                        item.status === 'success'
                          ? 'text-emerald-700 dark:text-emerald-300'
                          : item.status === 'uploading'
                            ? 'text-[var(--accent)]'
                            : 'text-red-700 dark:text-red-300',
                      ].join(' ')}
                    >
                      {item.status === 'success'
                        ? fmt(t.glossaryUploadTermsAdded, {
                            count: item.result?.termCount ?? 0,
                          })
                        : item.status === 'uploading'
                          ? t.glossaryUploading
                          : failureLabel(item)}
                    </span>
                  )}
                </div>

                {phase === 'results' && (
                  <div className="mt-1.5 pl-6 text-[11px] text-[var(--text-tertiary)]">
                    {item.status === 'success' && item.result?.skippedRows.length ? (
                      <span>
                        {item.result.skippedRows.length === 1
                          ? t.glossaryUploadRowSkipped
                          : fmt(t.glossaryUploadRowsSkipped, {
                              count: item.result.skippedRows.length,
                            })}
                      </span>
                    ) : null}
                    {hasDetails(item) && (
                      <button
                        type="button"
                        onClick={() => toggleDetails(item.id)}
                        className={[
                          'font-medium text-[var(--accent)] underline-offset-2 hover:underline',
                          item.status === 'success' && item.result?.skippedRows.length
                            ? 'ml-3'
                            : '',
                        ].join(' ')}
                      >
                        {item.detailsOpen
                          ? t.glossaryUploadHideDetails
                          : t.glossaryUploadViewDetails}
                      </button>
                    )}
                    {(item.status === 'failed' || item.status === 'local-invalid') && (
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => {
                          setReplacementId(item.id)
                          replacementInputRef.current?.click()
                        }}
                        className={[
                          'font-medium text-[var(--accent)] underline-offset-2 hover:underline disabled:opacity-50',
                          hasDetails(item) ? 'ml-3' : '',
                        ].join(' ')}
                      >
                        {t.glossaryUploadChooseAnother}
                      </button>
                    )}
                    {item.detailsOpen && (
                      <ul className="mt-2 space-y-1 rounded-md bg-[var(--bg-secondary)] p-2 text-[var(--text-secondary)]">
                        {item.status === 'success' && item.result
                          ? renderSkippedRows(item.result.skippedRows)
                          : renderErrorDetails(item)}
                      </ul>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {phase === 'selection' && files.some((item) => item.status === 'local-invalid') && (
          <p className="mt-3 text-xs leading-relaxed text-[var(--text-tertiary)]">
            {t.glossaryUploadFilesSkipped}
          </p>
        )}

        {phase === 'selection' ? (
          <div className="mt-5 grid grid-cols-2 gap-2 sm:gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={busy}
              className="h-10 rounded-xl border border-[var(--border-color)] bg-[var(--bg-primary)] text-sm font-semibold text-[var(--text-primary)] transition-colors hover:bg-[var(--bg-tertiary)] disabled:opacity-50"
            >
              {t.cancel}
            </button>
            <button
              type="button"
              onClick={() => void handleUpload()}
              disabled={pendingCount === 0 || busy}
              className="btn-primary h-10 rounded-xl text-sm font-semibold disabled:opacity-50"
            >
              {busy
                ? t.glossaryUploading
                : fmt(
                    pendingCount === 1 ? t.glossaryUploadFile : t.glossaryUploadFiles,
                    { count: pendingCount },
                  )}
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="btn-primary mt-5 h-10 w-full rounded-xl text-sm font-semibold disabled:opacity-50"
          >
            {t.glossaryUploadDone}
          </button>
        )}
      </div>
    </div>
  )
}
